import {
    BaseLinkPreviewObject,
    createLinkPreviewBorder,
    LinkPreviewType,
} from './BaseLinkPreviewObject';
import * as Constants from './constants';
import { PreviewSize } from './updatePreviewSize';
import BingRTPLinkPreview from 'owa-service/lib/contract/BingRTPLinkPreview';
import { getPalette } from 'owa-theme';
import { trace } from 'owa-trace';

const IMAGE_TARGET_HEIGHT_FULLSIZE = 160;
const IMAGE_MAX_WIDTH_FULLSIZE = 240;
const IDEAL_IMAGE_ASPECT_RATIO = IMAGE_MAX_WIDTH_FULLSIZE / IMAGE_TARGET_HEIGHT_FULLSIZE;

// This is default width that will be used on image with PreviewSize.Default
// The number is taken not to exceed the default image container height
// See setStyleForImageContainer
const IMAGE_DEFAULT_HEIGHT = 160;

const IMAGE_TARGET_HEIGHT_COMPACT = 106;
const IMAGE_MAX_WIDTH_COMPACT = 160;

export interface WebpageLinkPreviewObject extends BaseLinkPreviewObject {
    table?: HTMLTableElement;
    thumbnailImageContainer?: HTMLDivElement;
    thumbnailImageAnchor?: HTMLAnchorElement;
    thumbnailImageElement?: HTMLImageElement;
    titleContainer?: HTMLDivElement;
    titleUrlAnchor?: HTMLAnchorElement;
    metadataContainer?: HTMLDivElement;
    descriptionContainer?: HTMLDivElement;
}

export function createWebpageLinkPreviewObject(
    linkPreview: BingRTPLinkPreview,
    anchor: HTMLAnchorElement,
    inCompose: boolean
): WebpageLinkPreviewObject {
    let lp: WebpageLinkPreviewObject = {
        type: LinkPreviewType.WebpagePreview,
        description: linkPreview.Description,
        title: linkPreview.Title,
        url: linkPreview.Url,
        thumbnailImageUrl: linkPreview.ImageUrl,
        anchor: anchor,
    };

    createLinkPreviewBorder(Constants.LINK_PREVIEW_BORDER_WEBPAGE_TYPE, lp);
    createDomElementsForWebpageLinkPreview(lp, inCompose);
    return lp;
}

export async function rehydrateWebPageLinkPreview(
    div: HTMLDivElement
): Promise<BaseLinkPreviewObject> {
    div.style.position = 'relative';
    let tableElement = getElementStartingWithId(div, Constants.LINK_PREVIEW_CONTAINER_ID);
    let imageContainer = getElementStartingWithId(div, Constants.LINK_PREVIEW_IMAGE_CONTAINER_ID);
    let imageAnchor = getElementStartingWithId(div, Constants.LINK_PREVIEW_IMAGE_ANCHOR_ID);
    let imageElement = getElementStartingWithId(div, Constants.LINK_PREVIEW_IMAGE_THUMBNAIL_ID);
    let titleContainer = getElementStartingWithId(div, Constants.LINK_PREVIEW_TITLE_ID);
    let titleUrlAnchor = getElementStartingWithId(div, Constants.LINK_PREVIEW_URL_ANCHOR_ID);
    let metadataContainer = getElementStartingWithId(div, Constants.LINK_PREVIEW_METADATA_ID);
    let descriptionContainer = getElementStartingWithId(div, Constants.LINK_PREVIEW_DESCRIPTION_ID);

    let lp: WebpageLinkPreviewObject = {
        border: div,
        type: LinkPreviewType.WebpagePreview,
        description: descriptionContainer ? (<HTMLDivElement>descriptionContainer).innerHTML : null,
        title: titleUrlAnchor ? (<HTMLAnchorElement>titleUrlAnchor).innerText : null,
        url: titleUrlAnchor ? (<HTMLAnchorElement>titleUrlAnchor).href : null,
        thumbnailImageUrl: imageElement ? (<HTMLImageElement>imageElement).src : null,
        table: <HTMLTableElement>tableElement,
        thumbnailImageContainer: <HTMLDivElement>imageContainer,
        thumbnailImageAnchor: <HTMLAnchorElement>imageAnchor,
        thumbnailImageElement: <HTMLImageElement>imageElement,
        titleContainer: <HTMLDivElement>titleContainer,
        titleUrlAnchor: <HTMLAnchorElement>titleUrlAnchor,
        metadataContainer: <HTMLDivElement>metadataContainer,
        descriptionContainer: <HTMLDivElement>descriptionContainer,
    };

    // The relative position property on the image container may have been stripped after sent, and it is needed to ensure
    // that the play button is positioned properly, so add it back now
    if (lp.thumbnailImageContainer) {
        lp.thumbnailImageContainer.style.position = 'relative';
    }
    return Promise.resolve(lp);
}

export function updateWebpageLinkPreviewSize(
    linkPreview: WebpageLinkPreviewObject,
    previewSize: PreviewSize
) {
    if (linkPreview.thumbnailImageElement?.naturalWidth) {
        setImageSize(
            linkPreview.thumbnailImageElement,
            linkPreview.thumbnailImageContainer,
            previewSize
        );
    }
}

function getElementStartingWithId(div: HTMLDivElement, id: string): Element {
    let nodeListOfElements = div.querySelectorAll(`[id^=${id}]`);
    return nodeListOfElements && nodeListOfElements.length > 0 ? nodeListOfElements[0] : null;
}

export function createDomElementsForWebpageLinkPreview(
    lp: WebpageLinkPreviewObject,
    inCompose: boolean
) {
    addTableContainerToBorder(lp);

    let tableRow = document.createElement('tr');
    tableRow.vAlign = 'top';
    tableRow.style.borderSpacing = '0px';

    lp.table.appendChild(tableRow);
    setStyleForTable(lp.table, inCompose);

    // Add thumbnail image
    if (lp.thumbnailImageUrl) {
        let imageTableCell = document.createElement('td');
        tableRow.appendChild(imageTableCell);
        addImageToTableCell(imageTableCell, lp, inCompose);
    }

    let bodyTableCell = document.createElement('td');
    bodyTableCell.style.width = '100%';

    // Add title
    if (lp.title) {
        addTitleToTableCell(bodyTableCell, lp, inCompose);
    }

    // Add description
    if (lp.description) {
        addDescriptionToTableCell(bodyTableCell, lp);
    }

    // Add metadata
    addMetadataToTableCell(bodyTableCell, lp);

    tableRow.appendChild(bodyTableCell);
}

function addTableContainerToBorder(lp: WebpageLinkPreviewObject) {
    lp.table = document.createElement('table');
    lp.table.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_CONTAINER_ID
    );
    lp.table.setAttribute('role', 'presentation');
    lp.border.appendChild(lp.table);
}

function addImageToTableCell(
    tableCell: HTMLTableCellElement,
    lp: WebpageLinkPreviewObject,
    inCompose: boolean
) {
    lp.thumbnailImageContainer = document.createElement('div');
    lp.thumbnailImageContainer.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_IMAGE_CONTAINER_ID
    );
    setStyleForImageContainer(lp.thumbnailImageContainer);

    lp.thumbnailImageAnchor = document.createElement('a');
    fixLink(lp.thumbnailImageAnchor, inCompose);

    lp.thumbnailImageAnchor.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_IMAGE_ANCHOR_ID
    );
    lp.thumbnailImageAnchor.href = lp.url;

    lp.thumbnailImageElement = document.createElement('img');
    lp.thumbnailImageElement.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_IMAGE_THUMBNAIL_ID
    );
    lp.thumbnailImageElement.alt = '';
    setStyleForImageElement(
        lp.thumbnailImageUrl,
        lp.thumbnailImageElement,
        lp.thumbnailImageContainer,
        PreviewSize.Standard
    );

    lp.thumbnailImageAnchor.appendChild(lp.thumbnailImageElement);
    lp.thumbnailImageContainer.appendChild(lp.thumbnailImageAnchor);
    tableCell.appendChild(lp.thumbnailImageContainer);
}

function addTitleToTableCell(
    tableCell: HTMLTableCellElement,
    lp: WebpageLinkPreviewObject,
    inCompose: boolean
) {
    lp.titleContainer = document.createElement('div');
    lp.titleContainer.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_TITLE_ID
    );
    setStyleForTitleContainer(lp.titleContainer);

    lp.titleUrlAnchor = document.createElement('a');
    fixLink(lp.titleUrlAnchor, inCompose);

    lp.titleUrlAnchor.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_URL_ANCHOR_ID
    );
    lp.titleUrlAnchor.href = lp.url;
    lp.titleUrlAnchor.text = lp.title;
    setStyleForTitleUrlAnchor(lp.titleUrlAnchor);

    lp.titleContainer.appendChild(lp.titleUrlAnchor);
    tableCell.appendChild(lp.titleContainer);
}

function addMetadataToTableCell(tableCell: HTMLTableCellElement, lp: WebpageLinkPreviewObject) {
    lp.metadataContainer = document.createElement('div');

    lp.metadataContainer.textContent = lp.titleUrlAnchor.hostname;
    lp.metadataContainer.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_METADATA_ID
    );
    setStyleForMetadataContainer(lp.metadataContainer);

    tableCell.appendChild(lp.metadataContainer);
}

function addDescriptionToTableCell(tableCell: HTMLTableCellElement, lp: WebpageLinkPreviewObject) {
    lp.descriptionContainer = document.createElement('div');
    lp.descriptionContainer.textContent = lp.description;
    lp.descriptionContainer.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_DESCRIPTION_ID
    );
    setStyleForDescriptionContainer(lp.descriptionContainer);

    tableCell.appendChild(lp.descriptionContainer);
}

// STYLES
function setStyleForTable(table: HTMLTableElement, inCompose: boolean) {
    table.style.padding = '12px';
    table.style.width = '100%';
    table.style.borderWidth = '1px';
    table.style.borderStyle = 'solid';
    table.style.borderColor = '#c8c8c8'; // ms‐bgColor‐neutralTertiaryAlt
    table.style.borderRadius = '2px';
    if (inCompose) {
        table.style.paddingRight = '36px';
    }
}

function setStyleForImageContainer(imageContainer: HTMLDivElement) {
    imageContainer.style.position = 'relative';
    imageContainer.style.marginRight = '12px';
    imageContainer.style.height = '160px';
    imageContainer.style.overflow = 'hidden';
}

function setStyleForImageElement(
    src: string,
    imageElement: HTMLImageElement,
    imageContainer: HTMLDivElement,
    previewSize: PreviewSize
) {
    if (imageElement.complete && imageElement.naturalWidth !== 0) {
        setImageSize(imageElement, imageContainer, previewSize);
    } else {
        imageElement.style.display = 'none';
        imageElement.src = src;
        setImageSize(imageElement, imageContainer, PreviewSize.Default);
        imageElement.onload = (onLoadEvent: Event): void => {
            setImageSize(<HTMLImageElement>onLoadEvent.target, imageContainer, previewSize);
            imageElement.style.display = 'block';
        };
        imageElement.onerror = (onErrorEvent: Event): void => {
            trace.warn('Error loading image for link preview');
        };
    }
}

function setImageSize(
    imageElement: HTMLImageElement,
    imageContainer: HTMLDivElement,
    previewSize: PreviewSize
) {
    // If the previewSize is 'default' it means the image likely hasn't loaded yet, so just set it to the default width
    // so it is a reasonable size even if the image isn't loaded by the time the email is sent.
    if (previewSize === PreviewSize.Default) {
        imageElement.height = IMAGE_DEFAULT_HEIGHT;
        return;
    }

    let targetHeight: number;
    let targetWidth: number;
    switch (previewSize) {
        case PreviewSize.Standard:
            targetWidth = IMAGE_MAX_WIDTH_FULLSIZE;
            targetHeight = IMAGE_TARGET_HEIGHT_FULLSIZE;
            break;
        case PreviewSize.Compact:
            targetWidth = IMAGE_MAX_WIDTH_COMPACT;
            targetHeight = IMAGE_TARGET_HEIGHT_COMPACT;
            break;
    }

    let newHeight: number;
    let newWidth: number;

    let imageAspectRatio: number =
        imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0
            ? imageElement.naturalWidth / imageElement.naturalHeight
            : IDEAL_IMAGE_ASPECT_RATIO;
    if (imageElement.naturalWidth == imageElement.naturalHeight) {
        // Square image
        newWidth = targetHeight;
        newHeight = targetHeight;
    } else if (imageElement.naturalWidth > imageElement.naturalHeight) {
        // Wider
        newWidth = targetWidth;
        newHeight = targetWidth / imageAspectRatio;
        imageContainer.style.width = targetWidth + 'px';
        imageContainer.style.height = newHeight + 'px';
    } else {
        // Taller
        newWidth = targetHeight * imageAspectRatio;
        newHeight = targetHeight;
        imageContainer.style.width = targetWidth + 'px';
        imageContainer.style.height = newHeight + 'px';
    }

    imageElement.width = newWidth;
    imageElement.height = newHeight;
}

function setStyleForTitleContainer(titleContainer: HTMLDivElement) {
    titleContainer.style.fontSize = '21px';
    titleContainer.style.fontWeight = '300';
    titleContainer.style.marginRight = '40px';
    titleContainer.style.fontFamily =
        '"wf_segoe-ui_light","Segoe UI Light","Segoe WP Light","Segoe UI","Segoe WP",Tahoma,Arial,sans-serif';
    titleContainer.style.marginBottom = '12px';
    titleContainer.style.marginRight = '8px';
}

function setStyleForTitleUrlAnchor(titleUrlAnchor: HTMLAnchorElement) {
    titleUrlAnchor.style.textDecoration = 'none';
    titleUrlAnchor.style.color = getPalette().themePrimary;
}

function setStyleForMetadataContainer(metadataContainer: HTMLDivElement) {
    metadataContainer.style.fontSize = '14px';
    metadataContainer.style.fontWeight = '400';
    metadataContainer.style.color = '#a6a6a6';
    metadataContainer.style.fontFamily =
        '"wf_segoe-ui_normal","Segoe UI","Segoe WP",Tahoma,Arial,sans-serif';
}

function setStyleForDescriptionContainer(descriptionContainer: HTMLDivElement) {
    descriptionContainer.style.fontSize = '14px';
    descriptionContainer.style.maxHeight = '100px';
    descriptionContainer.style.color = '#666666';
    descriptionContainer.style.fontFamily =
        '"wf_segoe-ui_normal","Segoe UI","Segoe WP",Tahoma,Arial,sans-serif';
    descriptionContainer.style.marginBottom = '12px';
    descriptionContainer.style.marginRight = '8px';
    descriptionContainer.style.overflow = 'hidden';
}

function fixLink(anchor: HTMLAnchorElement, inCompose: boolean) {
    // In compose, suppress the tooltip saying "Ctrl+Click to follow link" since a single click will work
    // Set the target so that the link opens in a new tab in both compose and reading pane
    if (inCompose) {
        anchor.onmouseenter = function () {
            anchor.removeAttribute('title');
        };
    }
    anchor.target = '_blank';
}
